'use client';

import { Button, Highlight, Typography } from '@curology/ui-components-web';
import { CalendarIcon } from 'radiance-ui/lib/icons';
import { useState } from 'react';

import { useRouter } from '@/hooks/useRouter';
import { type VisitRecommendations } from '@/services/visitsService/types';
import { useCart } from '@/utils/shopify/hooks/useCart';

import { ProductCard } from './productCard';
import { ProductRadioOption } from './productRadioOption';
import { SectionHeader } from './sectionHeader';

const ProductSelectionForm = ({
  recommendation,
}: {
  recommendation: VisitRecommendations;
}) => {
  const { cart } = useCart({});
  const router = useRouter();

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!cart) {
      throw new Error('Cart is not defined');
    }

    router.away(cart.checkoutUrl);
  };

  const [activeHeroRecommendation, setActiveHeroRecommendation] = useState(
    recommendation.coreRx[0],
  );

  return (
    <form onSubmit={onSubmit}>
      {activeHeroRecommendation && (
        <ProductCard
          key={activeHeroRecommendation.product}
          productId={activeHeroRecommendation.product}
          productVariantId={activeHeroRecommendation.defaultVariant}
          aspectRatio="3:2"
          required
        />
      )}

      {recommendation.coreRx.length > 1 && (
        <div className="cur-mt-8">
          <SectionHeader>
            We offer other customized Rx formulas you can swap in for your trial
            offer.
          </SectionHeader>
          <div className="cur-space-y-4 cur-mt-4">
            {recommendation.coreRx
              .filter(
                (coreRx) => coreRx.product !== activeHeroRecommendation.product,
              )
              .map((coreRx) => (
                <ProductRadioOption
                  productId={coreRx.product}
                  defaultVariantId={coreRx.defaultVariant}
                  key={coreRx.product}
                  onChange={() => setActiveHeroRecommendation(coreRx)}
                />
              ))}
          </div>
        </div>
      )}

      {recommendation.routine && recommendation.routine.length > 0 && (
        <>
          <SectionHeader className="cur-mt-8">
            For best results, pair your Rx formula with these routine essentials
            chosen for your skin and goals.
          </SectionHeader>
          <div className="cur-mt-4 cur-grid cur-gap-4 cur-grid-cols-2">
            {recommendation.routine.map((routine) => (
              <ProductCard
                key={routine.product}
                productId={routine.product}
                productVariantId={routine.defaultVariant}
                aspectRatio="4:5"
              />
            ))}
          </div>
        </>
      )}
      {recommendation.addon && recommendation.addon.length > 0 && (
        <>
          <Typography size="h3" className="cur-mt-8">
            Add more to your box!
          </Typography>
          <div className="cur-mt-2 cur-grid cur-gap-4 cur-grid-cols-2">
            {recommendation.addon.map((addon) => (
              <ProductCard
                key={addon.product}
                productId={addon.product}
                productVariantId={addon.defaultVariant}
                aspectRatio="4:5"
              />
            ))}
          </div>
        </>
      )}
      <div className="cur-mt-8 cur-flex cur-items-center">
        <Button className="cur-w-full" type="submit">
          Check out now
        </Button>
      </div>

      <Highlight className="cur-mt-4" size="sm">
        Placing your order authorizes a temporary hold on your payment method.
        After your dermatology consultation is complete, your payment method
        will be charged.
      </Highlight>

      <div className="cur-mt-4 cur-flex cur-items-center cur-justify-center">
        <CalendarIcon className="cur-mr-2 cur-flex-shrink-0" />
        <Typography size="body">
          After your trial ends, we&apos;ll automatically send you full-size
          products every 60 days. Edit your subscription box or cancel anytime.
        </Typography>
      </div>
    </form>
  );
};

export { ProductSelectionForm };
